/* =====================================================================
 * MarkdownRenderer — 블록 기반 점진적 수식 렌더링 (모바일 최적화)
 *
 * 핵심 기법:
 * 1. 단락(블록) 단위 분할 → 완성된 블록만 Temml(MathML) 렌더링 + React.memo 캐시
 * 2. 진행 중인 마지막 블록만 경량 렌더링 (Temml 없이)
 * 3. 스트리밍 종료 시 마지막 1블록만 Temml 처리 → 최소 연산
 * 4. 콘텐츠 쓰로틀링 (~30ms) → 불필요 리렌더 최소화
 * 5. 코드 블록에 Syntax highlight + 복사 버튼
 *
 * Temml vs KaTeX:
 *   - 번들 크기 ~60% 감소, CSS ~66% 감소
 *   - MathML 네이티브 출력 → 브라우저 기본 렌더링 활용
 *   - 모바일(React Native WebView)에서 훨씬 빠른 수식 렌더링
 *
 * 렌더링 흐름:
 *   스트리밍 중  → [MathBlock₀] [MathBlock₁] ... [PlainBlock(active)]
 *   스트리밍 완료 → [MathBlock₀] [MathBlock₁] ... [MathBlock_last]
 *   → 이전 블록은 memo 캐시, 마지막 블록만 Temml 1회 렌더
 * ===================================================================== */

import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { rehypeTemml } from '@auw/workspace-ui';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { sanitizeSvg } from '@auw/workspace-utils';
import { Copy, Check, Terminal, FileCode, Table2, TextQuote, ExternalLink, Eye, Code2, Download, Workflow, Maximize2, Minus, Plus, RotateCcw, X, FileImage } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* 스트리밍 콘텐츠 쓰로틀 훅                                             */
/* ------------------------------------------------------------------ */

const STREAM_THROTTLE_MS = 30;

/**
 * 스트리밍 중 콘텐츠를 일정 간격(ms)으로 쓰로틀하여
 * 토큰 단위 리렌더를 줄이고, 스트리밍이 아닐 때는 즉시 반영.
 */
function useThrottledContent(content: string, active: boolean, ms: number): string {
  const [value, setValue] = useState(content);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastRef = useRef(0);

  useEffect(() => {
    if (!active) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setValue(content);
      lastRef.current = 0;
      return;
    }

    const now = Date.now();
    const elapsed = now - lastRef.current;

    if (elapsed >= ms) {
      lastRef.current = now;
      setValue(content);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        lastRef.current = Date.now();
        setValue(content);
      }, ms - elapsed);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [content, active, ms]);

  return value;
}

/* ------------------------------------------------------------------ */
/* Styled wrapper — markdown 요소에 대한 전역 스타일                       */
/* ------------------------------------------------------------------ */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const copyPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

const Wrapper = styled.div<{ $streaming?: boolean }>`
  /* Base */
  font-size: ${({ theme }) => theme.markdown.fontSize};
  line-height: ${({ theme }) => theme.markdown.lineHeight};
  word-break: break-word;
  overflow-wrap: break-word;

  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 1.2em;
    margin-bottom: 0.5em;
    font-weight: ${({ theme }) => theme.markdown.headingWeight};
    line-height: ${({ theme }) => theme.markdown.headingLineHeight};
    &:first-child { margin-top: 0; }
  }
  h1 { font-size: ${({ theme }) => theme.markdown.h1}; }
  h2 { font-size: ${({ theme }) => theme.markdown.h2}; }
  h3 { font-size: ${({ theme }) => theme.markdown.h3}; }

  /* Paragraph */
  p {
    margin: 0.6em 0;
    &:first-child { margin-top: 0; }
    &:last-child { margin-bottom: 0; }
  }

  /* Lists */
  ul, ol {
    padding-left: ${({ theme }) => theme.markdown.listPadLeft};
    margin: 0.5em 0;
  }
  li {
    margin: 0.2em 0;
  }
  li > p {
    margin: 0;
  }

  /* Links */
  a {
    color: ${({ theme }) => theme.colors.textLink};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  /* Blockquote — mdComponents에서 BlockquoteWrapper로 대체 */

  /* Horizontal rule */
  hr {
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    margin: 1em 0;
  }

  /* Table — mdComponents에서 TableWrapper로 대체 */

  /* Inline code */
  code:not(pre code) {
    background: ${({ theme }) => theme.colors.bgCode};
    color: ${({ theme }) => theme.colors.textPrimary};
    padding: ${({ theme }) => theme.markdown.inlineCodePad};
    border-radius: 4px;
    font-size: ${({ theme }) => theme.markdown.inlineCodeFontSize};
    font-family: ${({ theme }) => theme.fonts.mono};
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    object-fit: contain;
    display: block;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
    margin: 0.5em 0;
  }

  /* Temml (MathML) */
  math {
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.5em 0;
  }
  math[display="block"] {
    display: block;
    text-align: center;
    margin: 0.5em 0;
  }

  /* ── 모바일 반응형 ── */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.markdownMobile.fontSize};
    line-height: ${({ theme }) => theme.markdownMobile.lineHeight};

    h1 { font-size: ${({ theme }) => theme.markdownMobile.h1}; }
    h2 { font-size: ${({ theme }) => theme.markdownMobile.h2}; }
    h3 { font-size: ${({ theme }) => theme.markdownMobile.h3}; }

    ul, ol { padding-left: ${({ theme }) => theme.markdownMobile.listPadLeft}; }

    code:not(pre code) {
      padding: ${({ theme }) => theme.markdownMobile.inlineCodePad};
      font-size: ${({ theme }) => theme.markdownMobile.inlineCodeFontSize};
    }
  }

  /* 기본값: 블록 등장 애니메이션 활성 */
  --_block-anim-duration: 0.35s;

  ${({ $streaming }) =>
    $streaming &&
    css`
      /* 스트리밍 중 불완전한 수식 숨김 */
      .temml-error {
        display: none;
      }
      /* 스트리밍 중 slideDown 애니메이션 비활성화 → 레이아웃 시프트 제거 */
      --_block-anim-duration: 0s;
    `}
`;

/* ------------------------------------------------------------------ */
/* Code block — premium 디자인                                          */
/* ------------------------------------------------------------------ */

const CodeBlockWrapper = styled.div`
  /* ── 인라인 스타일(SyntaxHighlighter) 전달용 CSS 변수 ── */
  --_code-font-size: ${({ theme }) => theme.markdown.codeFontSize};
  --_code-line-height: ${({ theme }) => theme.markdown.codeLineHeight};
  --_code-pad: ${({ theme }) => theme.markdown.codePad};
  --_code-pad-ln: ${({ theme }) => theme.markdown.codePadLn};
  --_code-ln-font-size: ${({ theme }) => theme.markdown.headerFontSize};

  position: relative;
  margin: 1em 4px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  overflow: hidden;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#1a1b2e' : '#fafbfc'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)'
      : '0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)'};
  animation: ${slideDown} var(--_block-anim-duration, 0.35s) cubic-bezier(0.22, 1, 0.36, 1);
  transition: box-shadow ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 4px 16px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.3)'
        : '0 4px 20px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)'};
  }

  /* 코드 영역 커스텀 스크롤바 */
  pre {
    &::-webkit-scrollbar {
      height: 6px;
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
      border-radius: 3px;
      &:hover {
        background: ${({ theme }) =>
          theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)'};
      }
    }
  }

  /* ── 모바일 반응형 ── */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    pre {
      &::-webkit-scrollbar {
        height: 3px;
        width: 3px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 2px;
      }
    }
    --_code-font-size: ${({ theme }) => theme.markdownMobile.codeFontSize};
    --_code-pad: ${({ theme }) => theme.markdownMobile.codePad};
    --_code-pad-ln: ${({ theme }) => theme.markdownMobile.codePadLn};
    --_code-ln-font-size: ${({ theme }) => theme.markdownMobile.headerFontSize};
    margin: 0.75em 2px;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  }
`;

const CodeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.markdown.codeHeaderPad};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : 'rgba(0,0,0,0.03)'};
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'};
  font-size: ${({ theme }) => theme.markdown.headerFontSize};
  font-family: ${({ theme }) => theme.fonts.mono};
  user-select: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.markdownMobile.codeHeaderPad};
    font-size: ${({ theme }) => theme.markdownMobile.headerFontSize};
  }
`;

const LanguageBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)'};
  font-size: ${({ theme }) => theme.markdown.badgeFontSize};
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: lowercase;

  svg {
    opacity: 0.4;
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 4px;
    font-size: ${({ theme }) => theme.markdownMobile.badgeFontSize};
  }
`;

const CopyBtn = styled.button<{ $copied?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.markdown.badgeFontSize};
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ $copied }) =>
    $copied ? 'rgba(16, 163, 127, 0.15)' : 'transparent'};
  color: ${({ $copied, theme }) =>
    $copied
      ? '#10a37f'
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.4)'
        : 'rgba(0,0,0,0.4)'};

  svg {
    transition: all ${({ theme }) => theme.transitions.fast};
    ${({ $copied }) => $copied && css`animation: ${copyPulse} 0.3s ease;`}
  }

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2px 8px;
    gap: 4px;
    font-size: ${({ theme }) => theme.markdownMobile.badgeFontSize};
  }
`;

/* ------------------------------------------------------------------ */
/* Mermaid — 미리보기/다운로드 관련 스타일                                 */
/* ------------------------------------------------------------------ */

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.markdown.badgeFontSize};
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ $active }) =>
    $active ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
  color: ${({ $active, theme }) =>
    $active
      ? '#6366f1'
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.4)'
        : 'rgba(0,0,0,0.4)'};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2px 8px;
    gap: 4px;
    font-size: ${({ theme }) => theme.markdownMobile.badgeFontSize};
  }
`;

const MermaidPreviewContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5em 1em;
  min-height: 100px;
  overflow: auto;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#1e1f35' : '#ffffff'};

  svg {
    max-width: 100%;
    height: auto;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1em 0.5em;
    min-height: 80px;
  }
`;

const MermaidErrorMsg = styled.div`
  padding: 1em;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,100,100,0.8)' : 'rgba(200,0,0,0.7)'};
  font-size: 0.85em;
  font-family: ${({ theme }) => theme.fonts.mono};
`;

const MermaidLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2em;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'};
  font-size: 0.85em;
`;

/* ------------------------------------------------------------------ */
/* Mermaid Lightbox — 풀스크린 확대 뷰                                   */
/* ------------------------------------------------------------------ */

const lightboxFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const MermaidLightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${lightboxFadeIn} 0.2s ease;
`;

const MermaidLightboxClose = styled.button`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10001;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
  }
`;

const MermaidLightboxViewport = styled.div`
  flex: 1;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

const MermaidLightboxSvgWrap = styled.div`
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const MermaidLightboxToolbar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 12px;
  background: rgba(30, 30, 40, 0.85);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const LightboxToolBtn = styled.button<{ $accent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  transition: all 0.15s;
  background: ${({ $accent }) => $accent ? 'rgba(99, 102, 241, 0.2)' : 'transparent'};
  color: ${({ $accent }) => $accent ? '#a5b4fc' : 'rgba(255, 255, 255, 0.6)'};
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const LightboxZoomLabel = styled.span`
  min-width: 48px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  color: rgba(255, 255, 255, 0.75);
  user-select: none;
`;

const LightboxDivider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.12);
  margin: 0 4px;
`;

/* ------------------------------------------------------------------ */
/* Table — premium 디자인                                              */
/* ------------------------------------------------------------------ */

const TableWrapper = styled.div`
  position: relative;
  margin: 1em 0;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  overflow: hidden;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#1a1b2e' : '#fafbfc'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15)'
      : '0 2px 12px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.03)'};
  animation: ${slideDown} var(--_block-anim-duration, 0.3s) cubic-bezier(0.22, 1, 0.36, 1);
  transition: box-shadow ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 4px 16px rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.25)'
        : '0 4px 20px rgba(0,0,0,0.07), 0 2px 6px rgba(0,0,0,0.03)'};
  }

  /* 수평 스크롤 */
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
    border-radius: 3px;
    &:hover {
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)'};
    }
  }

  /* 모바일: 수평 스크롤바 슬림 */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    &::-webkit-scrollbar {
      height: 3px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 2px;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0;
    font-size: ${({ theme }) => theme.markdown.tableFontSize};
    display: table;
  }

  thead {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.028)'};
  }

  th {
    padding: ${({ theme }) => theme.markdown.tableCellPad};
    font-weight: 600;
    font-size: ${({ theme }) => theme.markdown.tableHeaderFontSize};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)'};
    border: none;
    border-bottom: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'};
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: ${({ theme }) => theme.markdown.tableCellPad};
    border: none;
    border-bottom: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.75)'};
    font-size: ${({ theme }) => theme.markdown.tableFontSize};
    line-height: 1.55;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tbody tr {
    transition: background ${({ theme }) => theme.transitions.fast};

    &:nth-child(even) {
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)'};
    }

    &:hover {
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)'};
    }
  }

  /* ── 모바일 반응형 ── */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: 0.75em 0;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};

    table { font-size: ${({ theme }) => theme.markdownMobile.tableFontSize}; }
    th {
      padding: ${({ theme }) => theme.markdownMobile.tableCellPad};
      font-size: ${({ theme }) => theme.markdownMobile.tableHeaderFontSize};
    }
    td {
      padding: ${({ theme }) => theme.markdownMobile.tableCellPad};
      font-size: ${({ theme }) => theme.markdownMobile.tableFontSize};
    }
  }
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.markdown.codeHeaderPad};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : 'rgba(0,0,0,0.03)'};
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'};
  font-size: ${({ theme }) => theme.markdown.headerFontSize};
  font-family: ${({ theme }) => theme.fonts.mono};
  user-select: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.markdownMobile.codeHeaderPad};
    font-size: ${({ theme }) => theme.markdownMobile.headerFontSize};
  }
`;

const TableBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)'};
  font-size: ${({ theme }) => theme.markdown.badgeFontSize};
  font-weight: 500;
  letter-spacing: 0.02em;

  svg {
    opacity: 0.4;
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 4px;
    font-size: ${({ theme }) => theme.markdownMobile.badgeFontSize};
  }
`;

/* ------------------------------------------------------------------ */
/* Blockquote — premium 디자인 (코드/테이블 카드와 통일)                   */
/* ------------------------------------------------------------------ */

const BlockquoteWrapper = styled.div`
  position: relative;
  margin: 1em 4px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  overflow: hidden;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#1a1b2e' : '#fafbfc'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15)'
      : '0 2px 12px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.03)'};
  animation: ${slideDown} var(--_block-anim-duration, 0.3s) cubic-bezier(0.22, 1, 0.36, 1);
  transition: box-shadow ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 4px 16px rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.25)'
        : '0 4px 20px rgba(0,0,0,0.07), 0 2px 6px rgba(0,0,0,0.03)'};
  }

  /* ── 모바일 반응형 ── */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: 0.75em 2px;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  }
`;

const BlockquoteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.markdown.codeHeaderPad};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : 'rgba(0,0,0,0.03)'};
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'};
  font-size: ${({ theme }) => theme.markdown.headerFontSize};
  font-family: ${({ theme }) => theme.fonts.mono};
  user-select: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.markdownMobile.codeHeaderPad};
    font-size: ${({ theme }) => theme.markdownMobile.headerFontSize};
  }
`;

const BlockquoteBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)'};
  font-size: ${({ theme }) => theme.markdown.badgeFontSize};
  font-weight: 500;
  letter-spacing: 0.02em;

  svg {
    opacity: 0.4;
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 4px;
    font-size: ${({ theme }) => theme.markdownMobile.badgeFontSize};
  }
`;

const BlockquoteBody = styled.div`
  padding: ${({ theme }) => theme.markdown.codePad};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)'};
  font-style: italic;
  line-height: 1.7;

  p {
    margin: 0.3em 0;
    &:first-child { margin-top: 0; }
    &:last-child { margin-bottom: 0; }
  }

  /* 중첩 인용 */
  blockquote {
    margin: 0.6em 0;
    padding: 0.6em 1em;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(0,0,0,0.025)'};
    border: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.markdownMobile.codePad};
  }
`;

/* ------------------------------------------------------------------ */
/* Blockquote Block component                                          */
/* ------------------------------------------------------------------ */

const BlockquoteBlock: React.FC<{
  children: React.ReactNode;
}> = React.memo(({ children }) => {
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const handleCopy = useCallback(async () => {
    if (!bodyRef.current) return;
    const text = bodyRef.current.innerText.trim();
    const ok = await copyTextToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  return (
    <BlockquoteWrapper>
      <BlockquoteHeader>
        <BlockquoteBadge>
          <TextQuote size={12} strokeWidth={1.8} />
          quote
        </BlockquoteBadge>
        <CopyBtn onClick={handleCopy} $copied={copied}>
          {copied ? (
            <>
              <Check size={12} strokeWidth={2.5} /> 복사됨
            </>
          ) : (
            <>
              <Copy size={12} strokeWidth={1.8} /> 복사
            </>
          )}
        </CopyBtn>
      </BlockquoteHeader>
      <BlockquoteBody ref={bodyRef}>{children}</BlockquoteBody>
    </BlockquoteWrapper>
  );
});
BlockquoteBlock.displayName = 'BlockquoteBlock';

/* ------------------------------------------------------------------ */
/* Source Chip — 출처 링크 칩 렌더링                                      */
/* ------------------------------------------------------------------ */

/** 날짜 패턴: (YYYY.MM.DD), (YYYY.MM), (YYYY) 등 */
const SOURCE_DATE_RE = /\((\d{4}(?:[.\-/]\d{1,2}(?:[.\-/]\d{1,2})?)?)\)/;

/** URL에서 도메인 추출 */
function extractDomain(url: string): string {
  try {
    const { hostname } = new URL(url);
    return hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** 출처 텍스트에서 제목과 날짜 분리 */
function parseSourceText(text: string): { title: string; date: string } {
  const dateMatch = text.match(SOURCE_DATE_RE);
  if (dateMatch) {
    const date = dateMatch[1];
    const title = text
      .replace(dateMatch[0], '')
      .replace(/\s*[-–—/]\s*$/, '')
      .replace(/^\s*[-–—/]\s*/, '')
      .trim();
    return { title, date };
  }
  return { title: text.trim(), date: '' };
}

/**
 * 출처 칩 렌더링 대상 판별:
 * - 외부 링크(http/https)
 * - 링크 텍스트가 URL 자체가 아닌 경우 (제목이 있는 링크)
 */
function isSourceLink(href: string | undefined, text: string): boolean {
  if (!href) return false;
  const isExternal = href.startsWith('http://') || href.startsWith('https://');
  if (!isExternal) return false;
  // 텍스트가 URL 자체이면 일반 링크로 유지
  const trimmed = text.trim();
  if (trimmed === href || trimmed === href.replace(/\/$/, '')) return false;
  // 텍스트가 너무 짧으면 (예: "여기", "link") 일반 링크로 유지
  if (trimmed.length < 4) return false;
  return true;
}

const SourceChipLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: min(280px, 70%);
  height: 28px;
  padding: 0 10px 0 6px;
  margin: 2px 2px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'};
  text-decoration: none !important;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8rem;
  line-height: 1;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.2)'
        : '0 1px 4px rgba(0,0,0,0.06)'};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ChipIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  flex-shrink: 0;

  svg {
    color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'};
  }
`;

const ChipLabel = styled.span`
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ChipDivider = styled.span`
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'};
  flex-shrink: 0;
  font-size: 0.7rem;
`;

const ChipSub = styled.span`
  flex-shrink: 100;
  min-width: 0;
  font-size: 0.7rem;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)'};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SourceChip: React.FC<{
  href: string;
  text: string;
}> = React.memo(({ href, text }) => {
  const { title } = parseSourceText(text);
  const domain = extractDomain(href);

  return (
    <SourceChipLink href={href} target="_blank" rel="noopener noreferrer" title={text}>
      <ChipIcon>
        <ExternalLink size={10} strokeWidth={2} />
      </ChipIcon>
      <ChipLabel>{title}</ChipLabel>
      {domain && (
        <>
          <ChipDivider>·</ChipDivider>
          <ChipSub>{domain}</ChipSub>
        </>
      )}
    </SourceChipLink>
  );
});
SourceChip.displayName = 'SourceChip';

/* ------------------------------------------------------------------ */
/* Clipboard fallback utilities                                        */
/* ------------------------------------------------------------------ */

/**
 * 텍스트를 클립보드에 복사 (Clipboard API + 레거시 fallback)
 * - 1차: navigator.clipboard.writeText (보안 컨텍스트 필요)
 * - 2차: document.execCommand('copy') (비보안 컨텍스트에서도 동작)
 */
async function copyTextToClipboard(text: string): Promise<boolean> {
  /* 1차: Clipboard API */
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fallthrough to legacy */
    }
  }

  /* 2차: 레거시 execCommand fallback */
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * 리치 텍스트(HTML + 일반 텍스트)를 클립보드에 복사 (fallback 포함)
 * - 1차: ClipboardItem API (HTML + plain text 동시 복사)
 * - 2차: Selection/Range 기반 HTML 복사 (execCommand)
 * - 3차: 일반 텍스트만 fallback 복사
 */
async function copyRichToClipboard(html: string, plainText: string): Promise<boolean> {
  /* 1차: ClipboardItem API */
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      /* fallthrough */
    }
  }

  /* 2차: Selection/Range 기반 HTML 복사 */
  try {
    const container = document.createElement('div');
    container.innerHTML = html;
    container.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
    document.body.appendChild(container);
    const range = document.createRange();
    range.selectNodeContents(container);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
      const ok = document.execCommand('copy');
      sel.removeAllRanges();
      document.body.removeChild(container);
      if (ok) return true;
    } else {
      document.body.removeChild(container);
    }
  } catch {
    /* fallthrough */
  }

  /* 3차: 일반 텍스트만 fallback */
  return copyTextToClipboard(plainText);
}

/** DOM 테이블에서 TSV(탭 구분) 텍스트 추출 — 일반 텍스트 에디터용 */
function extractTableTSV(tableEl: HTMLTableElement): string {
  const rows = tableEl.querySelectorAll('tr');
  const lines: string[] = [];
  rows.forEach((row) => {
    const cells = row.querySelectorAll('th, td');
    const vals: string[] = [];
    cells.forEach((cell) => vals.push((cell as HTMLElement).innerText.trim()));
    lines.push(vals.join('\t'));
  });
  return lines.join('\n');
}

/** DOM 테이블에서 깔끔한 HTML 테이블 생성 — Word/Docs 등 리치 텍스트 붙여넣기용 */
function extractTableHTML(tableEl: HTMLTableElement): string {
  const rows = tableEl.querySelectorAll('tr');
  let html =
    '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;font-family:sans-serif;font-size:13px;">';
  rows.forEach((row) => {
    html += '<tr>';
    const cells = row.querySelectorAll('th, td');
    cells.forEach((cell) => {
      const tag = cell.tagName.toLowerCase();
      const text = (cell as HTMLElement).innerText.trim();
      const style =
        tag === 'th'
          ? 'border:1px solid #bbb;padding:6px 10px;background:#f0f0f0;font-weight:600;'
          : 'border:1px solid #ddd;padding:6px 10px;';
      html += `<${tag} style="${style}">${text}</${tag}>`;
    });
    html += '</tr>';
  });
  html += '</table>';
  return html;
}

/* ------------------------------------------------------------------ */
/* Table Block component (with copy)                                   */
/* ------------------------------------------------------------------ */

const TableBlock: React.FC<{
  children: React.ReactNode;
  props: React.HTMLAttributes<HTMLTableElement>;
}> = React.memo(({ children, props }) => {
  const [copied, setCopied] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);

  const handleCopy = useCallback(async () => {
    if (!tableRef.current) return;
    const tsv = extractTableTSV(tableRef.current);
    const html = extractTableHTML(tableRef.current);
    const ok = await copyRichToClipboard(html, tsv);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  return (
    <TableWrapper>
      <TableHeader>
        <TableBadge>
          <Table2 size={12} strokeWidth={1.8} />
          table
        </TableBadge>
        <CopyBtn onClick={handleCopy} $copied={copied}>
          {copied ? (
            <>
              <Check size={12} strokeWidth={2.5} /> 복사됨
            </>
          ) : (
            <>
              <Copy size={12} strokeWidth={1.8} /> 서식 복사
            </>
          )}
        </CopyBtn>
      </TableHeader>
      <table ref={tableRef} {...props}>{children}</table>
    </TableWrapper>
  );
});
TableBlock.displayName = 'TableBlock';

/* ------------------------------------------------------------------ */
/* Code block icons                                                    */
/* ------------------------------------------------------------------ */

/** 언어별 아이콘 매핑 (없으면 기본 FileCode) */
const TERMINAL_LANGS = new Set(['bash', 'sh', 'zsh', 'shell', 'powershell', 'cmd', 'fish']);

const LangIcon: React.FC<{ lang: string }> = ({ lang }) => {
  const lower = lang.toLowerCase();
  if (TERMINAL_LANGS.has(lower)) {
    return <Terminal size={12} strokeWidth={1.8} />;
  }
  if (lower === 'mermaid') {
    return <Workflow size={12} strokeWidth={1.8} />;
  }
  return <FileCode size={12} strokeWidth={1.8} />;
};

/* ------------------------------------------------------------------ */
/* Code Block component                                                */
/* ------------------------------------------------------------------ */

/** 라인 수에 따라 라인넘버 표시 여부 결정 (5줄 이상이면 표시) */
const LINE_NUMBER_THRESHOLD = 5;

type SyntaxTheme = Record<string, React.CSSProperties>;

interface SyntaxAssets {
  Highlighter: React.ComponentType<{
    language?: string;
    style?: SyntaxTheme;
    showLineNumbers?: boolean;
    lineNumberStyle?: React.CSSProperties;
    customStyle?: React.CSSProperties;
    codeTagProps?: { style?: React.CSSProperties };
    wrapLongLines?: boolean;
    children?: React.ReactNode;
  }>;
  oneDark: SyntaxTheme;
  oneLight: SyntaxTheme;
}

let syntaxAssetsPromise: Promise<SyntaxAssets> | null = null;

function loadSyntaxAssets(): Promise<SyntaxAssets> {
  if (!syntaxAssetsPromise) {
    syntaxAssetsPromise = Promise.all([
      import('react-syntax-highlighter'),
      import('react-syntax-highlighter/dist/esm/styles/prism'),
    ]).then(([syntaxModule, styleModule]) => ({
      Highlighter: syntaxModule.Prism as SyntaxAssets['Highlighter'],
      oneDark: styleModule.oneDark as SyntaxTheme,
      oneLight: styleModule.oneLight as SyntaxTheme,
    }));
  }
  return syntaxAssetsPromise;
}

/* ------------------------------------------------------------------ */
/* Mermaid — lazy loading, 미리보기 & PNG 다운로드                        */
/* ------------------------------------------------------------------ */

let mermaidModulePromise: Promise<typeof import('mermaid')> | null = null;
let mermaidInitTheme: string | null = null;

function loadMermaid(theme: 'dark' | 'default' = 'default') {
  if (!mermaidModulePromise) {
    mermaidModulePromise = import('mermaid');
  }
  return mermaidModulePromise.then((m) => {
    if (mermaidInitTheme !== theme) {
      m.default.initialize({ startOnLoad: false, securityLevel: 'strict', theme });
      mermaidInitTheme = theme;
    }
    return m;
  });
}

let mermaidIdCounter = 0;

/**
 * Mermaid 코드 사전 처리: 노드 레이블 내 특수 문자로 인한 파싱 에러 방지.
 *
 * Mermaid는 (), [], {} 를 노드 모양 구문으로 해석하므로,
 * 레이블 안에 이런 문자가 섞이면 파싱 에러가 발생한다.
 * 예: Agent[LLM 에이전트\n(Agent)] → Agent["LLM 에이전트<br/>(Agent)"]
 */
function sanitizeMermaidCode(code: string): string {
  return code
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (
        !trimmed ||
        trimmed.startsWith('%%') ||
        trimmed.startsWith('classDef') ||
        trimmed.startsWith('class ') ||
        trimmed.startsWith('style ') ||
        trimmed.startsWith('click ') ||
        trimmed.startsWith('linkStyle') ||
        trimmed === 'end'
      ) {
        return line;
      }

      // subgraph 타이틀 내 특수문자 처리
      if (/^subgraph\s+/.test(trimmed)) {
        const rest = trimmed.replace(/^subgraph\s+/, '');
        if (rest && !rest.startsWith('"') && /[()[\]{}]/.test(rest)) {
          return line.replace(/(subgraph\s+)(.+)/, '$1"$2"');
        }
        return line;
      }

      // [...] 레이블 내 (){} 또는 \n → 따옴표로 감싸기
      line = line.replace(
        /(\w+)\[([^\]"]+)\]/g,
        (_match, id: string, content: string) => {
          if (!/[(){}]/.test(content) && !/\\n/.test(content)) return _match;
          return `${id}["${content.replace(/\\n/g, '<br/>')}"]`;
        },
      );

      // {...} 레이블 내 ()[] 또는 \n → 따옴표로 감싸기
      line = line.replace(
        /(\w+)\{([^}"]+)\}/g,
        (_match, id: string, content: string) => {
          if (!/[()[\]]/.test(content) && !/\\n/.test(content)) return _match;
          return `${id}{"${content.replace(/\\n/g, '<br/>')}"}`;
        },
      );

      // (...) 레이블 내 \n → 따옴표로 감싸기
      line = line.replace(
        /(\w+)\(([^)"]+)\)/g,
        (_match, id: string, content: string) => {
          if (!/\\n/.test(content)) return _match;
          return `${id}("${content.replace(/\\n/g, '<br/>')}")`;
        },
      );

      return line;
    })
    .join('\n');
}

/**
 * SVG 문자열에 viewBox 기반 명시적 width/height를 주입하여
 * Canvas 변환 시 정확한 크기를 보장한다.
 */
function getSvgWithExplicitSize(svgString: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const svgEl = doc.querySelector('svg');
  if (!svgEl) return svgString;

  const viewBox = svgEl.getAttribute('viewBox');
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/).map(Number);
    const vbW = parts[2];
    const vbH = parts[3];
    if (vbW && vbH) {
      svgEl.setAttribute('width', String(vbW));
      svgEl.setAttribute('height', String(vbH));
    }
  }
  return new XMLSerializer().serializeToString(svgEl);
}

const MermaidPreview: React.FC<{ code: string; isDark: boolean }> = React.memo(({ code, isDark }) => {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const renderIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const renderId = `mmd-${Date.now()}-${++renderIdRef.current}`;
    const safeCode = sanitizeMermaidCode(code);

    loadMermaid(isDark ? 'dark' : 'default')
      .then(async (m) => {
        if (cancelled) return;
        const { svg: rendered } = await m.default.render(renderId, safeCode);
        if (!cancelled) { setSvg(rendered); setError(''); }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Mermaid 렌더링 실패');
          setSvg('');
        }
      });

    return () => { cancelled = true; };
  }, [code, isDark]);

  if (error) return <MermaidErrorMsg>{error}</MermaidErrorMsg>;
  if (!svg) return <MermaidLoading>다이어그램 렌더링 중...</MermaidLoading>;
  return <MermaidPreviewContainer dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }} />;
});
MermaidPreview.displayName = 'MermaidPreview';

/* ------------------------------------------------------------------ */
/* MermaidLightbox — 풀스크린 확대 뷰 컴포넌트                            */
/* ------------------------------------------------------------------ */

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

function parseSvgDimensions(svgStr: string): { w: number; h: number } | null {
  const vbMatch = svgStr.match(/viewBox="([^"]+)"/);
  if (vbMatch) {
    const parts = vbMatch[1].split(/[\s,]+/).map(Number);
    if (parts[2] && parts[3]) return { w: parts[2], h: parts[3] };
  }
  const wMatch = svgStr.match(/width="([\d.]+)/);
  const hMatch = svgStr.match(/height="([\d.]+)/);
  if (wMatch && hMatch) return { w: parseFloat(wMatch[1]), h: parseFloat(hMatch[1]) };
  return null;
}

const MermaidLightbox: React.FC<{
  code: string;
  isDark: boolean;
  onClose: () => void;
}> = React.memo(({ code, isDark, onClose }) => {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);
  const renderIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const renderId = `mmd-lb-${Date.now()}-${++renderIdRef.current}`;
    const safeCode = sanitizeMermaidCode(code);

    loadMermaid(isDark ? 'dark' : 'default')
      .then(async (m) => {
        if (cancelled) return;
        const { svg: rendered } = await m.default.render(renderId, safeCode);
        if (!cancelled) { setSvg(rendered); setError(''); }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Mermaid 렌더링 실패');
      });

    return () => { cancelled = true; };
  }, [code, isDark]);

  const svgDims = useMemo(() => parseSvgDimensions(svg), [svg]);

  const handleFit = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const clampZoom = useCallback((z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)), []);

  const handleZoomIn = useCallback(() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP)), []);
  const handleZoomOut = useCallback(() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP)), []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((z) => clampZoom(z + delta));
  }, [clampZoom]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...pan };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [pan]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    setPan({
      x: panStart.current.x + (e.clientX - dragStart.current.x),
      y: panStart.current.y + (e.clientY - dragStart.current.y),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleDownloadPng = useCallback(async () => {
    try { await downloadMermaidAsPng(code, isDark); } catch { /* silent */ }
  }, [code, isDark]);

  const handleDownloadSvg = useCallback(async () => {
    try { await downloadMermaidAsSvg(code, isDark); } catch { /* silent */ }
  }, [code, isDark]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const zoomPercent = Math.round(zoom * 100);

  const wrapStyle: React.CSSProperties = svgDims
    ? {
        width: svgDims.w * zoom,
        height: svgDims.h * zoom,
        transform: `translate(${pan.x}px, ${pan.y}px)`,
      }
    : { transform: `translate(${pan.x}px, ${pan.y}px)` };

  return createPortal(
    <MermaidLightboxOverlay onClick={handleOverlayClick}>
      <MermaidLightboxClose onClick={onClose} title="닫기">
        <X size={18} strokeWidth={2} />
      </MermaidLightboxClose>

      <MermaidLightboxViewport
        ref={viewportRef}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {error ? (
          <MermaidErrorMsg>{error}</MermaidErrorMsg>
        ) : !svg ? (
          <MermaidLoading>다이어그램 렌더링 중...</MermaidLoading>
        ) : (
          <MermaidLightboxSvgWrap
            style={wrapStyle}
            dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
          />
        )}
      </MermaidLightboxViewport>

      <MermaidLightboxToolbar>
        <LightboxToolBtn onClick={handleZoomOut} disabled={zoom <= ZOOM_MIN} title="축소">
          <Minus size={14} strokeWidth={2} />
        </LightboxToolBtn>
        <LightboxZoomLabel>{zoomPercent}%</LightboxZoomLabel>
        <LightboxToolBtn onClick={handleZoomIn} disabled={zoom >= ZOOM_MAX} title="확대">
          <Plus size={14} strokeWidth={2} />
        </LightboxToolBtn>
        <LightboxToolBtn onClick={handleFit} title="맞춤">
          <RotateCcw size={13} strokeWidth={2} /> 맞춤
        </LightboxToolBtn>
        <LightboxDivider />
        <LightboxToolBtn onClick={handleDownloadSvg} title="SVG 다운로드">
          <Download size={13} strokeWidth={2} /> SVG
        </LightboxToolBtn>
        <LightboxToolBtn onClick={handleDownloadPng} $accent title="PNG 다운로드">
          <FileImage size={13} strokeWidth={2} /> PNG
        </LightboxToolBtn>
      </MermaidLightboxToolbar>
    </MermaidLightboxOverlay>,
    document.body,
  );
});
MermaidLightbox.displayName = 'MermaidLightbox';

async function renderMermaidSvg(code: string, isDark: boolean): Promise<string> {
  const safeCode = sanitizeMermaidCode(code);
  const m = await loadMermaid(isDark ? 'dark' : 'default');
  const renderId = `mmd-dl-${Date.now()}-${++mermaidIdCounter}`;
  const { svg } = await m.default.render(renderId, safeCode);
  return svg;
}

async function downloadMermaidAsPng(code: string, isDark: boolean): Promise<void> {
  const rawSvg = await renderMermaidSvg(code, isDark);
  const svg = getSvgWithExplicitSize(rawSvg);

  const img = new Image();
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  await new Promise<void>((resolve, reject) => {
    img.onload = () => {
      const scale = 4;
      const w = img.naturalWidth || 800;
      const h = img.naturalHeight || 600;
      const canvas = document.createElement('canvas');
      canvas.width = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not supported')); return; }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = isDark ? '#1a1b2e' : '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, w, h);

      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('PNG conversion failed')); return; }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'mermaid-diagram.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        resolve();
      }, 'image/png');
    };
    img.onerror = () => reject(new Error('SVG rendering failed'));
    img.src = svgDataUrl;
  });
}

async function downloadMermaidAsSvg(code: string, isDark: boolean): Promise<void> {
  const svg = await renderMermaidSvg(code, isDark);
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'mermaid-diagram.svg';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

/* ------------------------------------------------------------------ */
/* Code Block component                                                */
/* ------------------------------------------------------------------ */

const CodeBlock: React.FC<{
  language: string;
  code: string;
}> = React.memo(({ language, code }) => {
  const [copied, setCopied] = useState(false);
  const [syntaxAssets, setSyntaxAssets] = useState<SyntaxAssets | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const theme = useTheme();
  const isDark = theme.mode === 'dark';
  const lineCount = code.split('\n').length;
  const showLineNumbers = lineCount >= LINE_NUMBER_THRESHOLD;
  const displayLang = language || 'text';
  const isMermaid = displayLang === 'mermaid';

  const handleCopy = useCallback(async () => {
    const ok = await copyTextToClipboard(code);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [code]);

  const handleDownloadPng = useCallback(async () => {
    try { await downloadMermaidAsPng(code, isDark); } catch { /* silent */ }
  }, [code, isDark]);

  const handleDownloadSvg = useCallback(async () => {
    try { await downloadMermaidAsSvg(code, isDark); } catch { /* silent */ }
  }, [code, isDark]);

  const handleOpenLightbox = useCallback(() => setShowLightbox(true), []);
  const handleCloseLightbox = useCallback(() => setShowLightbox(false), []);

  useEffect(() => {
    let cancelled = false;
    loadSyntaxAssets()
      .then((assets) => {
        if (!cancelled) setSyntaxAssets(assets);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <CodeBlockWrapper>
      <CodeHeader>
        <LanguageBadge>
          <LangIcon lang={displayLang} />
          {displayLang}
        </LanguageBadge>
        <HeaderActions>
          {isMermaid && (
            <>
              <ActionBtn onClick={() => setShowPreview((v) => !v)} $active={showPreview}>
                {showPreview ? (
                  <><Code2 size={12} strokeWidth={1.8} /> 코드</>
                ) : (
                  <><Eye size={12} strokeWidth={1.8} /> 미리보기</>
                )}
              </ActionBtn>
              {showPreview && (
                <ActionBtn onClick={handleOpenLightbox}>
                  <Maximize2 size={12} strokeWidth={1.8} /> 확대
                </ActionBtn>
              )}
              <ActionBtn onClick={handleDownloadSvg}>
                <Download size={12} strokeWidth={1.8} /> SVG
              </ActionBtn>
              <ActionBtn onClick={handleDownloadPng}>
                <FileImage size={12} strokeWidth={1.8} /> PNG
              </ActionBtn>
            </>
          )}
          <CopyBtn onClick={handleCopy} $copied={copied}>
            {copied ? (
              <>
                <Check size={12} strokeWidth={2.5} /> 복사됨
              </>
            ) : (
              <>
                <Copy size={12} strokeWidth={1.8} /> 복사
              </>
            )}
          </CopyBtn>
        </HeaderActions>
      </CodeHeader>
      {showLightbox && isMermaid && (
        <MermaidLightbox code={code} isDark={isDark} onClose={handleCloseLightbox} />
      )}
      {isMermaid && showPreview ? (
        <MermaidPreview code={code} isDark={isDark} />
      ) : syntaxAssets ? (
        <syntaxAssets.Highlighter
          language={displayLang}
          style={isDark ? syntaxAssets.oneDark : syntaxAssets.oneLight}
          showLineNumbers={showLineNumbers}
          lineNumberStyle={{
            minWidth: '2.5em',
            paddingRight: '1em',
            color: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.18)',
            fontSize: 'var(--_code-ln-font-size)',
            userSelect: 'none' as const,
            textAlign: 'right' as const,
            borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'}`,
            marginRight: '1.2em',
          }}
          customStyle={{
            margin: 0,
            padding: showLineNumbers ? 'var(--_code-pad-ln)' : 'var(--_code-pad)',
            fontSize: 'var(--_code-font-size)',
            lineHeight: 'var(--_code-line-height)',
            borderRadius: 0,
            background: 'transparent',
            overflowX: 'auto' as const,
          }}
          codeTagProps={{
            style: {
              fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', Menlo, monospace",
              fontVariantLigatures: 'common-ligatures',
            },
          }}
          wrapLongLines={!showLineNumbers}
        >
          {code}
        </syntaxAssets.Highlighter>
      ) : (
        <pre
          style={{
            margin: 0,
            padding: showLineNumbers ? 'var(--_code-pad-ln)' : 'var(--_code-pad)',
            fontSize: 'var(--_code-font-size)',
            lineHeight: 'var(--_code-line-height)',
            borderRadius: 0,
            background: 'transparent',
            overflowX: 'auto',
          }}
        >
          <code
            style={{
              fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', Menlo, monospace",
              fontVariantLigatures: 'common-ligatures',
              whiteSpace: 'pre',
            }}
          >
            {code}
          </code>
        </pre>
      )}
    </CodeBlockWrapper>
  );
});
CodeBlock.displayName = 'CodeBlock';

/* ------------------------------------------------------------------ */
/* 모듈-레벨 플러그인 배열 & 컴포넌트 (참조 안정성 보장)                    */
/* ------------------------------------------------------------------ */

const remarkWithMath = [[remarkGfm, { singleTilde: false }], remarkMath];
const remarkPlain = [[remarkGfm, { singleTilde: false }]];
const _sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    'math', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac', 'msqrt',
    'mover', 'munder', 'mtable', 'mtr', 'mtd', 'mtext', 'mspace',
    'semantics', 'annotation',
  ],
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] ?? []), 'className', 'style'],
  },
};
const rehypeWithMath = [rehypeTemml, rehypeRaw, [rehypeSanitize, _sanitizeSchema]];
const rehypePlain = [rehypeRaw, [rehypeSanitize, _sanitizeSchema]];

/* eslint-disable @typescript-eslint/no-explicit-any */
const mdComponents: any = {
  /**
   * pre 오버라이드:
   * react-markdown v9는 fenced code block을 <pre><code class="language-xxx">…</code></pre>로 렌더링.
   * code 컴포넌트가 이미 CodeBlock(div)을 반환하므로 외부 <pre> 래핑을 제거하여
   * <pre><div>…<pre>…</pre></div></pre> 중첩 방지.
   */
  pre: ({
    children,
    node: _node,
    ...props
  }: React.HTMLAttributes<HTMLPreElement> & { node?: unknown }) => <>{children}</>,
  code: ({
    className,
    children,
    node: _node,
    ...props
  }: React.HTMLAttributes<HTMLElement> & { node?: unknown }) => {
    const match = /language-(\w+)/.exec(className || '');
    const code = String(children).replace(/\n$/, '');
    if (!match) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
    return <CodeBlock language={match[1]} code={code} />;
  },
  table: ({
    children,
    node: _node,
    ...props
  }: React.HTMLAttributes<HTMLTableElement> & { node?: unknown }) => (
    <TableBlock props={props}>{children as React.ReactNode}</TableBlock>
  ),
  blockquote: ({
    children,
    node: _node,
    ...props
  }: React.HTMLAttributes<HTMLQuoteElement> & { node?: unknown }) => (
    <BlockquoteBlock>{children as React.ReactNode}</BlockquoteBlock>
  ),
  a: ({
    href,
    children,
    node: _node,
    ...props
  }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) => {
    const text = typeof children === 'string' ? children : String(children ?? '');
    const isExternal = href?.startsWith('http://') || href?.startsWith('https://');

    if (href && isSourceLink(href, text)) {
      return <SourceChip href={href} text={text} />;
    }

    return (
      <a href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...props}>
        {children}
      </a>
    );
  },
};
/* eslint-enable @typescript-eslint/no-explicit-any */

/** 완성된 블록: Temml(MathML) 포함 렌더링, React.memo 로 캐싱 */
const MathBlock = React.memo<{ content: string }>(({ content }) => (
  <ReactMarkdown
    remarkPlugins={remarkWithMath as any}
    rehypePlugins={rehypeWithMath as any}
    components={mdComponents}
  >
    {content}
  </ReactMarkdown>
));
MathBlock.displayName = 'MathBlock';

/** 진행 중 블록: Temml 없이 경량 렌더링 */
const PlainBlock = React.memo<{ content: string }>(({ content }) => (
  <ReactMarkdown
    remarkPlugins={remarkPlain as any}
    rehypePlugins={rehypePlain as any}
    components={mdComponents}
  >
    {content}
  </ReactMarkdown>
));
PlainBlock.displayName = 'PlainBlock';

/* ------------------------------------------------------------------ */
/* Streaming safety: 불완전한 수식 블록 감지 및 제거                        */
/* ------------------------------------------------------------------ */

/**
 * 스트리밍 중 불완전한 마크다운 블록을 정리.
 * - remarkMath가 PlainBlock에서 비활성이므로 수식($) 정제는 불필요
 * - 닫히지 않은 코드 블록(```)만 제거하면 충분
 */
function sanitizeStreamingMarkdown(text: string): string {
  // 열려있는 코드 블록 ``` 처리
  const tripleBacktickCount = (text.match(/```/g) || []).length;
  if (tripleBacktickCount % 2 !== 0) {
    const lastIdx = text.lastIndexOf('```');
    return text.slice(0, lastIdx);
  }

  return text;
}

/* ------------------------------------------------------------------ */
/* Emphasis 전처리: **bold** + 특수문자 렌더링 실패 우회                    */
/* ------------------------------------------------------------------ */

/**
 * micromark(CommonMark) 파서는 ** 바로 옆에 특수문자가 올 때
 * left-flanking / right-flanking 규칙에 의해 강조를 인식하지 못함.
 * 예: **텍스트+특수** , **(괄호)**, **$수식$:** 등
 *
 * 해결:
 * 1) 코드 블록·인라인 코드만 플레이스홀더로 보호 (수식은 텍스트에 유지)
 * 2) ***...*** → <strong><em>…</em></strong>
 *    **...**   → <strong>…</strong>
 * 3) 코드 플레이스홀더 복원
 *
 * <strong> 는 CommonMark 인라인 HTML → 내부 $수식$ 은 remark-math 가 처리.
 *
 * 스트리밍 안전: 닫히지 않은 **는 regex가 매칭하지 않으므로
 *               미완성 토큰은 그대로 남아 다음 청크에서 처리됨.
 */
const CODE_ONLY_RE = /```[\s\S]*?```|`[^`\n]+`/g;

function preprocessEmphasis(text: string): string {
  // 1) 코드 블록·인라인 코드를 플레이스홀더로 치환 (수식은 그대로 유지)
  const codeSlots: string[] = [];
  const withPlaceholders = text.replace(CODE_ONLY_RE, (match) => {
    const idx = codeSlots.length;
    codeSlots.push(match);
    return `\x00CODE${idx}\x00`;
  });

  // 2) 강조 변환 — 수식($...$)을 포함한 전체 텍스트에서 동작
  const processed = withPlaceholders
    // ***bold-italic*** → <strong><em>…</em></strong>
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    // **bold** → <strong>…</strong>
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // 3) 코드 플레이스홀더 복원
  return processed.replace(/\x00CODE(\d+)\x00/g, (_, idx) => codeSlots[Number(idx)]);
}

/* ------------------------------------------------------------------ */
/* 블록 분할: 단락 경계 기준                                              */
/* ------------------------------------------------------------------ */

/**
 * 마크다운을 단락(빈 줄) 단위로 분할.
 * 코드 블록(```` ``` ````) 안의 빈 줄은 경계로 취급하지 않음.
 */
function splitToBlocks(text: string): string[] {
  const result: string[] = [];
  let buf = '';
  let fence = false;

  for (const line of text.split('\n')) {
    if (/^```/.test(line)) fence = !fence;

    if (!fence && line === '' && buf.trim()) {
      result.push(buf);
      buf = '';
    } else {
      buf += (buf ? '\n' : '') + line;
    }
  }
  if (buf.trim()) result.push(buf);
  return result;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface Props {
  content: string;
  isStreaming?: boolean;
}

/**
 * 블록 기반 점진적 렌더링:
 *
 * 스트리밍 중:
 *   content → sanitize → preprocessEmphasis → throttle → splitToBlocks
   *   → frozenBlocks (완성된 단락, Temml + memo 캐시)
   *   → activeBlock  (진행 중 단락, Temml 없이 경량 렌더)
 *
 * 스트리밍 완료:
 *   → 전체 블록이 frozen → 마지막 블록만 MathBlock 신규 마운트
   *   → 기존 블록들은 React.memo 캐시 히트 → Temml 재연산 0회
 */
export const MarkdownRenderer: React.FC<Props> = React.memo(({ content, isStreaming }) => {
  const safeContent = useMemo(
    () => {
      const sanitized = isStreaming ? sanitizeStreamingMarkdown(content) : content;
      return preprocessEmphasis(sanitized);
    },
    [content, isStreaming],
  );

  /* ── 스트리밍 중 쓰로틀: ~30ms 간격으로만 리렌더 ── */
  const displayContent = useThrottledContent(safeContent, !!isStreaming, STREAM_THROTTLE_MS);
  const hasCodeFence = useMemo(() => /```/.test(displayContent), [displayContent]);

  useEffect(() => {
    if (!hasCodeFence) return;
    void loadSyntaxAssets();
  }, [hasCodeFence]);

  /* ── 블록 분할: frozen (완성) + active (진행 중) ── */
  const { frozenBlocks, activeBlock } = useMemo(() => {
    const blocks = splitToBlocks(displayContent);

    if (!isStreaming || blocks.length === 0) {
      /* 스트리밍 완료 또는 빈 콘텐츠: 전부 frozen (Temml 렌더링) */
      return { frozenBlocks: blocks, activeBlock: '' };
    }

    if (blocks.length === 1) {
      /* 첫 단락 작성 중: frozen 없음, active만 (경량 렌더) */
      return { frozenBlocks: [] as string[], activeBlock: blocks[0] };
    }

    /* 마지막 블록만 active, 나머지는 frozen */
    return {
      frozenBlocks: blocks.slice(0, -1),
      activeBlock: blocks[blocks.length - 1],
    };
  }, [displayContent, isStreaming]);

  if (frozenBlocks.length === 0 && !activeBlock) return null;

  return (
    <Wrapper $streaming={isStreaming}>
      {/* 완성된 블록: Temml(MathML) 포함 렌더링 + React.memo 캐시 */}
      {frozenBlocks.map((block, i) => (
        <MathBlock key={i} content={block} />
      ))}
      {/* 진행 중 블록: Temml 없이 경량 렌더링 */}
      {activeBlock && <PlainBlock content={activeBlock} />}
    </Wrapper>
  );
});
MarkdownRenderer.displayName = 'MarkdownRenderer';

